import { Button, Menu, MenuItem, Checkbox, ListItemText } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useState } from "react";
import * as Sentry from "@sentry/react";

import { TableColumn } from "./types";
import { columnDefinitions } from "./columnDefinitions";

const toggleableColumnIds: TableColumn[] = [
  "external_ref_id",
  "status",
  "monitor_types",
  "last_alerted",
  "next_run",
];

const defaultHiddenColumnIds: TableColumn[] = ["external_ref_id"];

const localStorageKey = "monitorsTableHiddenColumns";

const getColumnLabel = (columnId: TableColumn) => {
  const column = columnDefinitions[columnId];
  if (!column || !("whenLoading" in column)) {
    return columnId; // Fallback to columnId if not found
  }
  return column.whenLoading.header as string;
};

export const getCachedHiddenColumnIds = () => {
  let cached: string | null = null;

  try {
    cached = localStorage.getItem(localStorageKey);
  } catch (e) {
    Sentry.captureException(e);
    console.error("Error accessing localStorage for hidden column IDs:", e);
    return defaultHiddenColumnIds;
  }

  if (!cached) {
    return defaultHiddenColumnIds;
  }

  try {
    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed)) {
      throw new Error("Cached object is not an array");
    }

    parsed.forEach((columnId: TableColumn) => {
      if (typeof columnId !== "string") {
        throw new Error("Cached column ID is not a string");
      }

      if (!(columnId in columnDefinitions)) {
        throw new Error("Cached column ID is not in columnDefinitions");
      }
    });

    return parsed;
  } catch (e) {
    Sentry.captureException(e);
    console.error("Error parsing cached hidden column IDs:", e);
    return defaultHiddenColumnIds;
  }
};

// There is built-in show/hide column functionality in react-table
// that I couldn't get working. If you need to do anything too serious here
// you should give it another try: https://tanstack.com/table/v8/docs/guide/column-visibility
export default function ShowHideColumns({
  hiddenColumnIds,
  setHiddenColumnIds,
}: {
  hiddenColumnIds: TableColumn[];
  setHiddenColumnIds: (columns: TableColumn[]) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleHideColumnId = (columnId: TableColumn) => {
    let newHidden;
    if (hiddenColumnIds.includes(columnId)) {
      newHidden = hiddenColumnIds.filter((id) => id !== columnId);
    } else {
      newHidden = [...hiddenColumnIds, columnId];
    }

    try {
      localStorage.setItem(localStorageKey, JSON.stringify(newHidden));
    } catch (e) {
      Sentry.captureException(e);
      console.error("Error saving hidden column IDs to localStorage:", e);
    }
    setHiddenColumnIds(newHidden);
  };

  return (
    <>
      <Button onClick={handleClick} endIcon={<KeyboardArrowDownIcon />}>
        Show/Hide Columns
      </Button>

      <Menu open={anchorEl !== null} anchorEl={anchorEl} onClose={handleClose}>
        {toggleableColumnIds.map((columnId) => (
          <MenuItem key={columnId} onClick={() => toggleHideColumnId(columnId)}>
            <Checkbox checked={!hiddenColumnIds.includes(columnId)} />
            <ListItemText primary={getColumnLabel(columnId)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
